import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement | null} */
  get details() {
    const details = this.querySelector('details');

    return details instanceof HTMLDetailsElement ? details : null;
  }

  /** @type {HTMLElement | null} */
  get summary() {
    const summary = this.details?.querySelector('summary');

    return summary instanceof HTMLElement ? summary : null;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const summary = this.summary;

    if (!this.details || !summary) return;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const details = this.details;
    if (!details) return;

    const isMobile = isMobileBreakpoint();

    details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    const details = this.details;
    const summary = this.summary;
    if (!details || !summary) return;

    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      details.open = false;
      summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

function moveStreetBrandBreadcrumbs() {
  const hub = document.querySelector('[data-bmw-street-hub], [data-audi-street-hub]');
  if (!hub) return;

  const breadcrumb = document.querySelector('.street-breadcrumbs');
  const programs = hub.querySelector('.bmw-street-programs, .audi-street-programs');

  if (!breadcrumb || !programs) return;
  if (breadcrumb.nextElementSibling === programs) return;

  hub.insertBefore(breadcrumb, programs);
}

function initStreetBrandFaqAccordions() {
  const lists = document.querySelectorAll('.bmw-faq-list, .audi-faq-list');

  lists.forEach((list) => {
    const items = Array.from(list.querySelectorAll('.bmw-faq-item, .audi-faq-item'));

    items.forEach((item) => {
      const icon = item.querySelector('summary span[aria-hidden="true"]');
      item.open = false;
      if (icon) icon.textContent = '+';

      if (item.dataset.streetFaqReady === 'true') return;
      item.dataset.streetFaqReady = 'true';

      item.addEventListener('toggle', () => {
        if (item.open) {
          items.forEach((otherItem) => {
            if (otherItem !== item) otherItem.open = false;
          });
        }

        items.forEach((faqItem) => {
          const faqIcon = faqItem.querySelector('summary span[aria-hidden="true"]');
          if (faqIcon) faqIcon.textContent = faqItem.open ? '×' : '+';
        });
      });
    });
  });
}

function injectStreetBrandFaqStyles() {
  if (document.getElementById('street-brand-faq-accordion-styles')) return;

  const style = document.createElement('style');
  style.id = 'street-brand-faq-accordion-styles';
  style.textContent = `
    .bmw-street-faq,
    .audi-street-faq {
      padding-top: 40px !important;
      padding-bottom: 40px !important;
    }

    .bmw-street-faq .bmw-street-section-head,
    .audi-street-faq .audi-street-section-head {
      margin-bottom: 18px;
    }

    .bmw-faq-item summary,
    .audi-faq-item summary {
      min-height: 48px;
      padding: 0;
    }

    .bmw-faq-item summary span,
    .audi-faq-item summary span {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      min-width: 20px;
      transform: none !important;
      font-size: 22px;
      font-weight: 400;
      line-height: 1;
    }

    .bmw-faq-item div,
    .audi-faq-item div {
      padding: 0 0 12px;
      line-height: 1.5;
    }

    @media (max-width: 749px) {
      .bmw-street-faq,
      .audi-street-faq {
        padding-top: 30px !important;
        padding-bottom: 30px !important;
      }

      .bmw-faq-item summary,
      .audi-faq-item summary {
        min-height: 44px;
      }
    }
  `;
  document.head.appendChild(style);
}

function initStreetBrandFaq() {
  moveStreetBrandBreadcrumbs();
  injectStreetBrandFaqStyles();
  initStreetBrandFaqAccordions();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStreetBrandFaq);
} else {
  initStreetBrandFaq();
}

document.addEventListener('shopify:section:load', initStreetBrandFaq);
